/**
 * A RFC 4122 version 4 UUID, using the platform generator when it exists.
 *
 * `crypto.randomUUID` needs Safari 15.4 and a secure context. Both matter here:
 *   the production browserslist query still resolves to iOS Safari builds below
 *   that, and a plain-HTTP origin (a dev server reached over the LAN) makes it
 *   `undefined` on any browser. Calling it directly throws a TypeError, and the
 *   two callers sit in the offline branch of their epics — which returns before
 *   the `handleAjaxError` guard is attached, so nothing catches it. In
 *   `redux-observable` an error reaching the merged output kills the single
 *   subscription, taking every epic with it: the app looks alive and stops
 *   responding until reload.
 *
 * `crypto.getRandomValues` has been available since Safari 6.1 and works in
 *   insecure contexts, so it covers every browser the app itself can run in —
 *   the effective floor is Safari 13.1, set by `ResizeObserver`. That is why
 *   there is no third tier: a `Math.random` path would only ever run on a
 *   browser where the app is already broken, and it would silently weaken a
 *   value that is about to be used as an idempotency key.
 */
export const randomUUID = (): string => {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Version 4 in the high nibble of byte 6, and the RFC 4122 variant in the
  //   top two bits of byte 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, byte =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
};
